import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import RouteMap from "@/components/RouteMap";
import { Check, Download } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";
import { categoryLabel, fmtDate, legRoute, legsCoveredLabel, moneyCents, phoneLines, shortAirport, signatureLines } from "@/lib/format";

const usDate = fmtDate;
const tripDates = (legs = []) => {
  const days = [...new Set(legs.map((l) => l.date).filter(Boolean))].sort();
  return days.length ? [...new Set([days[0], days[days.length - 1]])].map(usDate).join(" – ") : "";
};
const cityRoute = (legs = []) => (legs.length ? [legs[0].from_airport, ...legs.map((l) => l.to_airport)].map(shortAirport).join(" – ") : "");
const telFor = (line) => {
  const m = /^(Office|Mobile) (.+)$/.exec(line || "");
  if (!m) return null;
  const [num, ext = ""] = m[2].split(" ext. ");
  let digits = num.replace(/[^\d+]/g, "");
  if (digits.replace("+", "").length < 7) return null;
  if (!digits.startsWith("+") && digits.length === 10) digits = `+1${digits}`;
  return `tel:${digits}${ext.replace(/\D/g, "") ? `,${ext.replace(/\D/g, "")}` : ""}`;
};
const ContactLine = ({ line, testId }) => {
  const href = telFor(line);
  return href ? <a href={href} className="whitespace-nowrap underline decoration-[#C6A55C]/50 underline-offset-4 hover:text-[#C6A55C] transition-colors" data-testid={testId}>{line}</a> : <>{line}</>;
};
const AdvisorLine = ({ b }) => {
  const parts = [b.name, ...phoneLines(b), b.email].filter(Boolean);
  return parts.map((l, i) => <span key={i}>{i > 0 && "   ·   "}<ContactLine line={l} testId={`share-cover-call-${l.split(" ")[0].toLowerCase()}`} /></span>);
};
import AcceptDialog from "@/components/AcceptDialog";
import PhotoLightbox from "@/components/PhotoLightbox";
import { FlightTime } from "@/components/FlightTime";
import { fmtTime } from "@/components/TrackingBits";

function Breakdown({ it, testId }) {
  const cur = it.currency || "USD";
  const rows = [["Charter price", it.client_price], ["Taxes & fees", (it.fet || 0) + (it.segment_fees || 0)]];
  return (
    <dl className="mt-5 border-t border-white/[0.08] pt-3 text-sm" data-testid={testId}>
      {rows.map(([l, v]) => (
        <div key={l} className="flex justify-between py-1 text-zinc-400"><dt>{l}</dt><dd className="text-zinc-200">{moneyCents(v, cur)}</dd></div>
      ))}
      <div className="flex justify-between pt-2 mt-1 border-t border-white/[0.08] text-white"><dt>All-in total</dt><dd>{moneyCents(it.total_price ?? it.client_price, cur)}</dd></div>
    </dl>
  );
}

function Item({ it, i, selected, onSelect, allIn, legs }) {
  const [zoom, setZoom] = useState(null);
  const photos = it.photo_urls ? [["exterior", "Exterior"], ["interior", "Interior"]].map(([k, label]) => ({ k, label, src: `${BACKEND_URL}${it.photo_urls[k]}` })) : [];
  return (
    <article className={`rise lux-card overflow-hidden flex flex-col max-md:w-[84vw] max-md:shrink-0 max-md:snap-center ${selected ? "!border-[#5B7FD1]/70" : ""}`} style={{ animationDelay: `${i * 80}ms` }} data-testid={`share-option-${i + 1}`}>
      {it.photo_urls && (
        <div className="grid grid-cols-2 gap-px bg-white/[0.06]">
          {photos.map((ph, j) => (
            <button key={ph.k} type="button" className="block overflow-hidden cursor-zoom-in group" onClick={() => setZoom(j)} data-testid={`share-option-${i + 1}-${ph.k}-photo-btn`} aria-label={`Enlarge ${ph.label.toLowerCase()} photo`}>
              <img src={ph.src} alt={`${it.aircraft_type} ${ph.k}`} className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} data-testid={`share-option-${i + 1}-${ph.k}-photo`} />
            </button>
          ))}
        </div>
      )}
      <PhotoLightbox photos={photos} start={zoom} title={it.aircraft_type} onClose={() => setZoom(null)} />
      <div className="p-5 sm:p-7 flex flex-col flex-1">
        <div className="flex flex-col items-start gap-3">
          <div className="min-w-0 w-full">
            <p className="font-mono-data text-[11px] text-[#5B7FD1]">OPTION {String(i + 1).padStart(2, "0")}</p>
            <h3 className="font-serif-display text-2xl sm:text-3xl text-white mt-2 break-words">{it.aircraft_type}</h3>
            <p className="text-xs text-zinc-400 mt-1">{categoryLabel(it.aircraft_category)}</p>
            {legsCoveredLabel(it.legs_covered, legs) && <p className="text-[11px] uppercase tracking-[0.14em] text-[#9DB4EA] mt-2" data-testid={`share-option-${i + 1}-legs`}>{legsCoveredLabel(it.legs_covered, legs)}</p>}
          </div>
          <div className="shrink-0" data-testid={`share-option-${i + 1}-price-block`}>
            <p className="font-serif-display text-2xl lg:text-3xl text-white whitespace-nowrap" data-testid={`share-option-${i + 1}-price`}>{moneyCents(it.total_price ?? it.client_price, it.currency || "USD")}</p>
            <p className="text-[0.58rem] tracking-[0.2em] uppercase text-[#5B7FD1] mt-1" data-testid={`share-option-${i + 1}-price-label`}>{allIn ? "All-in price" : "Plus taxes & fees"}</p>
          </div>
        </div>
        <dl className="grid grid-cols-3 gap-4 mt-6 text-sm">
          {[["Seats", it.seats], ["YOM", it.year], ["YOR", it.year_refurbished], ["Est. flight time", it.flight_time && <FlightTime value={it.flight_time} testId={`share-option-${i + 1}-flight-time`} />]].map(([l, v]) => (
            <div key={l}><dt className="text-[0.58rem] tracking-[0.22em] uppercase text-zinc-500">{l}</dt><dd className="text-zinc-100 mt-1">{v || "—"}</dd></div>
          ))}
        </dl>
        {allIn && <Breakdown it={it} testId={`share-option-${i + 1}-breakdown`} />}
        {it.amenities && <p className="text-xs text-zinc-400 mt-4">{it.amenities}</p>}
        {it.client_note && <p className="text-sm text-zinc-300 mt-3">{it.client_note}</p>}
        <div className="mt-auto pt-6">
          {it.unavailable ? (
            <p className="text-[0.7rem] tracking-[0.2em] uppercase text-zinc-500" data-testid={`share-option-${i + 1}-unavailable`}>No longer available</p>
          ) : selected ? (
            <p className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.2em] uppercase text-[#5B7FD1]" data-testid={`share-option-${i + 1}-selected`}>
              <Check size={14} /> Your selection
            </p>
          ) : (
            <button className="frame-btn gold" onClick={() => onSelect(i)} data-testid={`share-option-${i + 1}-accept-btn`}>Request to Book</button>
          )}
        </div>
      </div>
    </article>
  );
}

const itemPrice = (it) => it.total_price ?? it.client_price;

function MixBuilder({ p, onBook }) {
  const [pick, setPick] = useState({});
  const legs = p.legs;
  const leggy = p.items.map((it, i) => ({ it, i })).filter(({ it }) => it.legs_covered?.length && !it.unavailable);
  if (legs.length < 2 || !legs.every((_, l) => leggy.some(({ it }) => it.legs_covered.includes(l)))) return null;
  const choose = (i) => {
    const cov = p.items[i].legs_covered;
    setPick((cur) => {
      const next = Object.fromEntries(Object.entries(cur).filter(([, v]) => !p.items[v].legs_covered.some((x) => cov.includes(x))));
      cov.forEach((l) => { next[l] = i; });
      return next;
    });
  };
  const chosen = [...new Set(Object.values(pick))].sort((a, b) => a - b);
  const done = legs.every((_, l) => pick[l] !== undefined);
  const cur = p.items[0]?.currency || "USD";
  return (
    <section id="share-mix" className="lux-card p-5 sm:p-7 mt-10" data-testid="share-mix-builder">
      <p className="eyebrow mb-1">Mix and match by leg</p>
      <p className="text-sm text-zinc-400 mb-4">Choose one aircraft for each leg and request them together as one trip.</p>
      {legs.map((l, li) => (
        <div key={li} className="py-3 border-t border-white/[0.06]">
          <p className="text-xs text-zinc-400 mb-2">Leg {li + 1} · {legRoute(l)} · {fmtDate(l.date)}</p>
          <div className="flex flex-wrap gap-2">
            {leggy.filter(({ it }) => it.legs_covered.includes(li)).map(({ it, i }) => (
              <button key={i} type="button" onClick={() => choose(i)} data-testid={`share-mix-leg-${li}-option-${i + 1}`}
                className={`px-3 py-2 text-left text-xs border transition-colors ${pick[li] === i ? "border-[#5B7FD1] bg-[#5B7FD1]/10 text-white" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>
                Option {i + 1} · {it.aircraft_type} · {moneyCents(itemPrice(it), it.currency || cur)}{it.legs_covered.length > 1 ? ` · covers legs ${it.legs_covered.map((x) => x + 1).join(", ")}` : ""}
              </button>
            ))}
          </div>
        </div>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-white/[0.06]">
        <p className="text-sm text-white" data-testid="share-mix-total">{done ? `Combined total ${moneyCents(chosen.reduce((s, i) => s + itemPrice(p.items[i]), 0), cur)}${p.all_in ? " all-in" : " plus taxes & fees"}` : "Pick an aircraft for every leg"}</p>
        <button type="button" className="frame-btn gold" disabled={!done} onClick={() => onBook(chosen)} data-testid="share-mix-book-btn">Request to book this combination</button>
      </div>
    </section>
  );
}

export default function SharePage() {
  useEffect(() => { document.title = "JetSet Group Inc"; }, []);
  const { token } = useParams();
  const [search, setSearch] = useSearchParams();
  const [preview] = useState(() => search.get("preview") === "1");
  const [p, setP] = useState(null);
  const [error, setError] = useState("");
  const [choosing, setChoosing] = useState(null);
  const [slide, setSlide] = useState(0);
  const onSlide = (e) => {
    const el = e.currentTarget;
    const w = el.firstElementChild?.offsetWidth || 1;
    setSlide(Math.round(el.scrollLeft / (w + 16)));
  };

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/share/${token}`, { params: preview ? { preview: true } : {} }).then((r) => setP(r.data)).catch(() => setError("This proposal link is no longer available."));
    const t = setInterval(() => {
      axios.get(`${BACKEND_URL}/api/share/${token}`, { params: { poll: true } }).then((r) => setP(r.data)).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, [token, preview]);

  useEffect(() => {
    if (!p || !search.has("book")) return;
    const raw = search.get("book");
    const i = Number(raw);
    if (/^\d+$/.test(raw) && p.items[i] && !p.items[i].unavailable) setChoosing(i);
    setSearch(preview ? { preview: "1" } : {}, { replace: true });
  }, [p, search, setSearch, preview]);

  if (error) return <div className="min-h-screen grid place-items-center text-zinc-400" data-testid="share-error">{error}</div>;
  if (!p) return <div className="min-h-screen grid place-items-center"><div className="jet-loader" /></div>;
  return (
    <div className="min-h-screen bg-[#090A0D] overflow-x-clip" data-testid="share-page">
      <header className="on-navy app-header">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-[72px] flex items-center justify-between">
          <img src="/brand/logo-silver.png" alt="JetSet Group Inc" className="h-12 w-auto" data-testid="share-brand" />
          <a href={`${BACKEND_URL}/api/share/${token}/pdf`} className="frame-btn !py-2.5" data-testid="share-download-pdf-btn"><Download size={13} /> PDF</a>
        </div>
      </header>
      <section className="on-navy relative" data-testid="share-hero">
        <img src={`${BACKEND_URL}/api/media/stock/cover.jpg`} alt="Private jet in flight" className="w-full aspect-[3/2] sm:aspect-auto sm:h-[380px] lg:h-[440px] object-cover object-[center_55%]" data-testid="share-cover-img" />
        <div className="h-[3px] bg-[#C6A55C]" />
        <div className="bg-[#0F1D3D]">
          <div className="max-w-5xl mx-auto px-6 sm:px-10 py-10 sm:py-14">
            <p className="text-[0.62rem] sm:text-xs tracking-[0.35em] uppercase font-bold text-[#C6A55C]">Private jet charter proposal</p>
            <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white uppercase leading-[1.15] mt-4" data-testid="share-cover-title">
              {p.client_name ? `Prepared for ${p.client_name}` : p.title}
            </h1>
            <p className="text-[#D9DCE3] text-base sm:text-lg mt-4" data-testid="share-cover-when">{[tripDates(p.legs), cityRoute(p.legs)].filter(Boolean).join("  ·  ")}</p>
            <p className="text-[0.6rem] sm:text-[0.68rem] tracking-[0.35em] uppercase font-bold text-[#C6A55C] mt-10">Your charter advisor</p>
            <p className="text-white text-sm mt-2 whitespace-pre-wrap" data-testid="share-cover-advisor"><AdvisorLine b={p.broker || {}} /></p>
          </div>
        </div>
      </section>
      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-10 sm:py-14">
        {p.message && <p className="text-zinc-400 text-sm sm:text-base mb-6 max-w-2xl whitespace-pre-line leading-relaxed">{p.message}</p>}
        <RouteMap legs={p.legs} points={p.route_points || {}} className="mb-6" testId="share-route-map" />
        <div className="lux-card divide-y divide-white/[0.06]" data-testid="share-itinerary">
          {p.legs.map((l, i) => (
            <div key={i} className="grid grid-cols-[40px_1fr_auto] gap-3 px-5 py-4 text-sm items-center">
              <span className="font-mono-data text-[11px] text-[#5B7FD1]">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-white" data-testid={`share-leg-${i}-route`}>{shortAirport(l.from_airport)} → {shortAirport(l.to_airport)}</span>
              <span className="text-zinc-400 text-xs">{fmtDate(l.date)}{l.time ? ` · ${fmtTime(l.time)}` : ""} · {l.pax} pax</span>
            </div>
          ))}
        </div>
        {p.accepted && (
          <div className="lux-card !border-[#5B7FD1]/50 mt-10 p-5 flex items-center gap-3 text-sm text-zinc-200" data-testid="share-accepted-banner">
            <Check size={16} className="text-[#5B7FD1] shrink-0" />
            Thank you. You selected {p.accepted.item_indexes?.length > 1 ? `a combination of Options ${p.accepted.item_indexes.map((i) => i + 1).join(" + ")}` : `Option ${p.accepted.item_index + 1}`}. {p.broker?.name || "Your charter advisor"} has been notified and will be in touch to confirm your booking.
          </div>
        )}
        {p.items.length > 1 && (
          <div className="md:hidden flex items-center justify-between mt-10 -mb-6 text-[11px] text-zinc-500" data-testid="share-swipe-hint">
            <span>Swipe to compare options</span>
            <span className="flex gap-1.5">{p.items.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === slide ? "w-5 bg-[#5B7FD1]" : "w-1.5 bg-zinc-400/50"}`} />)}</span>
          </div>
        )}
        <div onScroll={onSlide} data-testid="share-options-list"
          className="mt-10 max-md:flex max-md:gap-4 max-md:overflow-x-auto max-md:snap-x max-md:snap-mandatory max-md:-mx-4 max-md:px-4 max-md:pb-2 md:grid md:grid-cols-2 md:gap-5 [scrollbar-width:none]">{p.items.map((it, i) => <Item key={i} it={it} i={i} legs={p.legs} allIn={p.all_in} selected={(p.accepted?.item_indexes || [p.accepted?.item_index]).includes(i)}
          onSelect={it.legs_covered?.length && p.legs.length > 1 ? () => document.getElementById("share-mix")?.scrollIntoView({ behavior: "smooth" }) : setChoosing} />)}</div>
        <MixBuilder p={p} onBook={setChoosing} />
        <AcceptDialog open={choosing !== null} onOpenChange={(o) => { if (!o) setChoosing(null); }} token={token}
          item={Array.isArray(choosing) ? { aircraft_type: choosing.map((i) => p.items[i].aircraft_type).join(" + "), total_price: choosing.reduce((s, i) => s + itemPrice(p.items[i]), 0), currency: p.items[choosing[0]].currency }
            : choosing !== null ? p.items[choosing] : null}
          index={Array.isArray(choosing) ? choosing[0] : choosing ?? 0} indexes={Array.isArray(choosing) ? choosing : null} defaultName={p.client_name}
          onAccepted={(accepted) => setP((cur) => ({ ...cur, accepted }))} />
        <footer className="mt-16 pt-8 border-t border-white/[0.06] text-sm text-zinc-400" data-testid="share-contact">
          <p className="eyebrow mb-2">Your charter advisor</p>
          {signatureLines(p.broker || {}).map((l, i) => <p key={i} className={i === 0 ? "text-white" : "mt-0.5 text-xs"}><ContactLine line={l} testId={`share-signature-call-${l.split(" ")[0].toLowerCase()}`} /></p>)}
          <p className="text-[11px] text-zinc-600 mt-6" data-testid="share-disclaimer">All flights arranged by JetSet Group Inc. are operated by FAA Part 135 carriers unless noted. Operators providing services for JetSet Group Inc. clients must meet standards set forth by the Federal Aviation Administration (FAA). JetSet Group Inc. does not manage or operate aircraft on behalf of our clients. We represent our clients in finding aircraft for their needs while abiding by FAA Part 295. Pricing subject to availability and final confirmation. Flight times are estimates and may change with weather, winds and routing.</p>
        </footer>
      </main>
    </div>
  );
}
